// Satış Faturası / Sipariş / Teklif / İrsaliye formu kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'documents.salesInvoice';

export const salesInvoiceGuide = (t: TFunction): GuideContent => ({
  layout: 'stacked',
  title: t(`${G}.title`, 'Satış belgesi nasıl hazırlanır?'),
  intro: t(`${G}.intro`, 'Satış faturası, satış siparişi, satış teklifi ve irsaliye aynı formla hazırlanır. Kaydettiğinizde stok ve cariye etkisi belge türüne göre değişir. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık ve düğmeler'),
    form: t(`${G}.regions.form`, 'Cari ve belge bilgileri'),
    list: t(`${G}.regions.list`, 'Kalemler'),
    footer: t(`${G}.regions.footer`, 'Notlar ve toplamlar'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Belge türünü ve düğmeleri tanıyın'),
      text: t(`${G}.s1.text`, 'Başlık hangi belgede olduğunuzu gösterir: "Yeni Satış Faturası", "Yeni Satış Siparişi", "Yeni Satış Teklifi" veya "Yeni İrsaliye". Belge türü formu açtığınız yerden gelir (Faturalar, Siparişler, Teklifler veya İrsaliyeler sayfası).'),
      tips: [
        t(`${G}.s1.tip1`, 'Taslak Kaydet: formu yalnız bu tarayıcıda saklar; stok ve cariye dokunmaz. Form doluyken taslak ayrıca kendiliğinden kaydedilir; formu yeniden açınca "Kaydedilmiş Taslak Bulundu" penceresi taslağı yüklemek isteyip istemediğinizi sorar.'),
        t(`${G}.s1.tip2`, 'Kaydet / Güncelle: belgeyi kalıcı olarak kaydeder (etkileri 6. adımda).'),
        t(`${G}.s1.tip3`, 'Proje düğmesiyle belgeyi bir projeye bağlayabilirsiniz (isteğe bağlı).'),
      ],
    },
    {
      region: 'form',
      title: t(`${G}.s2.title`, '2. Cariyi ve belge bilgilerini girin'),
      text: t(`${G}.s2.text`, 'Yeni belgede cari seçim penceresi kendiliğinden açılır; müşteriyi seçin. Sağ tarafta belge numarası, tarih, vade tarihi, para birimi ve döviz kurunu girin. TL dışı para biriminde toplamın TL karşılığı da gösterilir.'),
      tips: [
        t(`${G}.s2.tip1`, 'Teklifte "Geçerlilik Tarihi" zorunludur; "Teklif Durumu" seçilir.'),
        t(`${G}.s2.tip2`, 'Siparişte "Teslim Tarihi" isteğe bağlıdır; "Sipariş Durumu" seçilir.'),
        t(`${G}.s2.tip3`, 'İrsaliyede "Fiyatları PDF/Yazdırmada Göster" düğmesiyle fiyatları çıktıda gizleyebilirsiniz.'),
      ],
    },
    {
      region: 'form',
      title: t(`${G}.s3.title`, '3. Faturada e-fatura bilgileri ve iade'),
      text: t(`${G}.s3.text`, 'Yalnız faturada "Fatura Profil Türü" ve "Fatura Tip Kodu" seçilir. Tip kodu iade seçilirse "İade Edilen Orijinal Fatura" alanı açılır: önce cariyi seçin, sonra listeden orijinal faturayı seçin.'),
      tips: [
        t(`${G}.s3.tip1`, 'İade faturasında orijinal fatura seçimi zorunludur; seçmeden kaydedilemez.'),
        t(`${G}.s3.tip2`, 'Kalemler kalan iade edilebilir miktarla otomatik dolar; kalan miktarı aşan iade kabul edilmez.'),
      ],
    },
    {
      region: 'list',
      title: t(`${G}.s4.title`, '4. Kalemleri ekleyin ve fiyatlandırın'),
      text: t(`${G}.s4.text`, '"Ürün Ekle" stok ürünü seçme penceresini açar (ürün adı, barkod veya stok kodu ile arayın, depo seçin). "Stoksuz Ürün Ekle" adını elle yazdığınız bir satır ekler. "Başka Faturadan" (siparişte "Başka Siparişten" vb.) başka bir belgenin kalemlerini kopyalar. "Toplu Depo Seç" tüm kalemlere aynı depoyu verir.'),
      tips: [
        t(`${G}.s4.tip1`, 'Her satırda Miktar, Fiyatı, KDV (Dahil / Hariç) ve KDV % girin. Bu alanlardan biri boşsa kayıt engellenir ve hangi kalemde neyin eksik olduğu yazılır.'),
        t(`${G}.s4.tip2`, 'İndirim sütununun başlığından yüzde (İndirim %) veya tutar seçilir; seçim tüm kalemler için geçerlidir.'),
        t(`${G}.s4.tip3`, 'Aynı ürünü aynı depodan yeniden eklerseniz yeni satır açılmaz, miktarı 1 artar.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s5.title`, '5. Toplamları kontrol edin'),
      text: t(`${G}.s5.text`, 'Toplamlar bölümünde Ara Toplam, varsa İndirim ve İndirim Sonrası Ara Toplam, her KDV oranı için ayrı KDV satırı ve Genel Toplam görünür. Toplamlar başlığındaki + düğmesiyle Tevkifat veya Stopaj ekleyebilirsiniz; eklenirse "Ödenecek Tutar" ayrıca gösterilir. Notlar alanına belgeyle ilgili açıklama yazabilirsiniz.'),
    },
    {
      region: 'header',
      title: t(`${G}.s6.title`, '6. Kaydedin: stok ve cariye etkisi'),
      text: t(`${G}.s6.text`, 'Kaydet için cari ve en az bir kalem gerekir. Belgenin stok ve cariye etkisi türüne göre değişir:'),
      tips: [
        t(`${G}.s6.tip1`, 'Satış faturası: kalemler stoktan düşülür ve müşterinin borcu artar. Cariye yazılan tutar Ödenecek Tutar\'dır (tevkifat / stopaj düşülmüş) ve TL\'ye çevrilerek kaydedilir. Fatura, stok ve cari birlikte kaydedilir; biri başarısız olursa hiçbiri yazılmaz.'),
        t(`${G}.s6.tip2`, 'İade tipli satış faturası ters çalışır: ürün stoğa geri girer, müşterinin borcu azalır.'),
        t(`${G}.s6.tip3`, 'Stoksuz ürün satırları ve deposu seçilmemiş satırlar stok hareketi oluşturmaz.'),
        t(`${G}.s6.tip4`, 'Satış siparişi: kaydederken "Sipariş kaydedilirken stoktan düşülsün mü?" diye sorulur. "Evet, Stoktan Düşülsün" ürünleri stoktan düşer ve geri alınamaz. Sipariş cari bakiyesini değiştirmez. Düzenlemede bu soru sorulmaz.'),
        t(`${G}.s6.tip5`, 'Teklif ve irsaliye stok ve cari bakiyesini değiştirmez; yalnız belge kaydedilir.'),
        t(`${G}.s6.tip6`, 'Faturayı tekliften, siparişten veya irsaliyeden oluşturduysanız kaynak belge "faturalandı" olarak işaretlenir.'),
        t(`${G}.s6.tip7`, 'Güncelle: eski stok ve cari hareketleri geri alınır, yeni hareketler yazılır. Hızlı satıştan gelen kapalı faturalar düzenlenemez.'),
      ],
    },
  ],
});
